/**
 * BIGGO iframe → host postMessage validation (pure functions).
 *
 * Ported from the legacy quebon-student-web `biggoLogic.js` rules: trust a message only when its
 * origin equals the origin of the iframe URL the backend returned, its source is the current iframe
 * window, and its payload has the expected type/event and value ranges. The completion message is
 * only a trigger to ask our backend to confirm with BIGGO; it is never a result by itself.
 */

export const RESULT_CONFIRM_DELAYS_MS = [1000, 2000, 4000] as const
export const MAX_FINAL_SCORE = 1_000_000

export interface BiggoMessageContext {
  expectedOrigin: string
  iframeWindow: Window | null | undefined
}

export interface BiggoStartedMessage {
  startedAt: number
  deadline: number
  serverNow: number
}

export interface BiggoCompletionMessage {
  finalScore: number
}

interface MessageLike {
  origin: string
  source: unknown
  data: unknown
}

function trustedPayload(event: MessageLike | null | undefined, context: BiggoMessageContext): Record<string, unknown> | null {
  if (!event || !context.iframeWindow) return null
  if (event.origin !== context.expectedOrigin || event.source !== context.iframeWindow) return null
  const payload = event.data
  if (!payload || typeof payload !== 'object') return null
  const record = payload as Record<string, unknown>
  if (record.type !== 'biggo-assessment') return null
  return record
}

const isMs = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

export function parseBiggoStartedMessage(
  event: MessageLike | null | undefined,
  context: BiggoMessageContext,
): BiggoStartedMessage | null {
  const payload = trustedPayload(event, context)
  if (!payload || payload.event !== 'started') return null
  const { startedAt, deadline, serverNow } = payload
  if (!isMs(startedAt) || !isMs(deadline) || !isMs(serverNow)) return null
  if (deadline <= startedAt) return null
  return { startedAt, deadline, serverNow }
}

export function parseBiggoCompletionMessage(
  event: MessageLike | null | undefined,
  context: BiggoMessageContext,
): BiggoCompletionMessage | null {
  const payload = trustedPayload(event, context)
  if (!payload || payload.event !== 'completed') return null
  const score = payload.finalScore
  if (!isMs(score) || score < 0 || score > MAX_FINAL_SCORE) return null
  return { finalScore: score }
}

/** Origin of the BIGGO iframe URL the backend returned; null when the URL is malformed. */
export function iframeOrigin(iframeUrl: string): string | null {
  try {
    return new URL(iframeUrl).origin
  } catch {
    return null
  }
}

/**
 * Remaining ms until the run deadline, using the clock skew observed in the started message
 * (`serverNow - clientNowAtMessage`). Never negative.
 */
export function remainingMs(started: BiggoStartedMessage, clientNowAtMessage: number, clientNow: number): number {
  const skew = started.serverNow - clientNowAtMessage
  return Math.max(0, started.deadline - (clientNow + skew))
}

/** ms → "m:ss", rounded up so the display does not show 0:00 while time remains. */
export function formatRemainingTime(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`
}

/** Delay before the n-th confirm retry (0-based); null when retries are exhausted. */
export function resultConfirmDelayMs(attemptIndex: number): number | null {
  return attemptIndex < RESULT_CONFIRM_DELAYS_MS.length ? RESULT_CONFIRM_DELAYS_MS[attemptIndex] : null
}
